import { useEffect, useMemo, useState } from "react";
import { Box } from "@mui/material";
import DynamicTable from "../../components/DynamicTable/DynamicTable";
import { excelFormateData, camelCaseToTitleCase } from "../../helpers/utils";
import TableTopToolBar from "../../Pages/Reports/TopToolBar";
import { useLocation } from "react-router-dom";
import moment from "moment";
import Export from "../../components/Export/ExportToXLSX";
import SnackBars from "../../components/snackbars/SnackBars";

const SitesReportTable = ({
  fetchData,
  exportFileNamePrefix = "Site Report",
}) => {
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const route = useLocation();
  const renderExportToolbar = route.pathname.includes("/reports/");

  const [sitesReport, setSitesReport] = useState([]);
  const [totalSitesReport, setTotalSitesReport] = useState(0);
  const [rowSelection, setRowSelection] = useState({});
  const [columns, setColumns] = useState({});
  const [filteredData, setFilteredData] = useState([]);
  const [columnFilters, setColumnFilters] = useState([]);
  const [isRefetching, setIsRefetching] = useState(false);
  const [taskDetails, setTaskDetails] = useState([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [allSiteExportData, setAllSiteExportData] = useState([]);
  const [allSitePageData, setAllSitePageData] = useState([]);

  useEffect(() => {
    const getData = setTimeout(() => {
      getReport();
    }, 500);
    return () => clearTimeout(getData);
  }, [pagination.pageIndex, pagination.pageSize, columnFilters]);

  const getReport = async () => {
    try {
      setIsRefetching(true);
      const allSiteReport = await fetchData(
        pagination.pageIndex + 1,
        pagination.pageSize,
        columnFilters
      );
      setSitesReport(allSiteReport?.data);
      setTotalSitesReport(allSiteReport?.totalCount);
      if (allSiteReport.totalCount > 0) setColumns(allSiteReport?.data[0]);
      setAllSiteExportData(allSiteReport?.allData);
      setAllSitePageData(allSiteReport?.data);
      setIsRefetching(false);
    } catch (error) {
      if (error.response && error.response.status === 404) {
        setSitesReport([]);
        setTotalSitesReport(0);
        setColumns({});
        setSnackbarMessage("No Data Found.");
        setSnackbarSeverity("warning");
      } else {
        setSnackbarMessage("Internal Server Error !");
        setSnackbarSeverity("error");
      }
      setSnackbarOpen(true);
    }
  };

  const memoisedTableColumns = useMemo(() => {
    const dynamicColumns = Object.keys(columns)
      .filter(
        (key) =>
          key !== "id" &&
          key !== "createdAt" &&
          key !== "updatedAt" &&
          key !== "updatedBy" &&
          key !== "activeFlag"
      )
      .map((key) => {
        const header = camelCaseToTitleCase(key);
        const width = Math.min(Math.max(header.length * 12, 100), 250);

        const columnDef = {
          accessorKey: key,
          header,
          size: width,
        };

        if (key === "omc") {
          columnDef.filterFn = "equals"; // You can use "equals" or a custom one
          columnDef.filterVariant = "select";
          columnDef.filterSelectOptions = ["BPCL", "IOCL", "HPCL"];
        }
        return columnDef;
      });
    return [...dynamicColumns];
  }, [columns, taskDetails]);

  const exportSelectedData = (data) => {
    const currentDate = moment().format("YYYY-MM-DD");
    const updatedData = excelFormateData(data);
    Export(updatedData, `${exportFileNamePrefix} - ${currentDate}.xlsx`);
  };

  const exportPageData = () => {
    const currentDate = moment().format("YYYY-MM-DD");
    const updatedData = excelFormateData(allSitePageData);
    Export(updatedData, `${exportFileNamePrefix} - ${currentDate}.xlsx`);
  };

  const exportAllData = async () => {
    const updatedData = excelFormateData(allSiteExportData);
    const currentDate = moment().format("YYYY-MM-DD");
    Export(updatedData, `${exportFileNamePrefix} - ${currentDate}.xlsx`);
  };

  const TopToolBarWithButtons = ({ table }) => {
    return (
      <TableTopToolBar
        table={table}
        exportPageData={exportPageData}
        exportSelectedData={exportSelectedData}
        exportAllData={exportAllData}
      />
    );
  };

  return (
    <Box width={"100%"}>
      <Box>
        <DynamicTable
          data={sitesReport}
          columns={memoisedTableColumns}
          totalSitesCount={totalSitesReport}
          setPagination={setPagination}
          pagination={pagination}
          isEnablePagination={true}
          isFilterEnable={true}
          isSearchBarEnable={true}
          isEnableSorting={true}
          isHeaderBackgroundColor={false}
          showTableTopToolbar={true}
          enableColumnActions={true}
          rowSelection={rowSelection}
          updateRowSelection={setRowSelection}
          TopToolBar={TopToolBarWithButtons}
          enableRowSelection={true}
          updateFilteredData={setFilteredData}
          columnFilters={columnFilters}
          setColumnFilters={setColumnFilters}
          isRefetching={isRefetching}
          isServerSideFiltering={true}
        />
      </Box>
      <SnackBars
        open={snackbarOpen}
        autoHideDuration={6000}
        handleClose={() => setSnackbarOpen(false)}
        title={snackbarMessage}
        type={snackbarSeverity}
      />
    </Box>
  );
};

export default SitesReportTable;
